import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { routes } from "consts";
import { AdminResourceDeleteModal, AdminSearchInput, PageMain } from "components/molecules";
import { PaziaButton, PaziaLoader, PaziaBox, PaziaBadge } from "components/atoms";
import { TanTable, useFilters, usePagination, useSorting, TanTableColumn } from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { useBlogPosts, BlogPostListItemApi, blogApi } from "api/shop";
import { useDeleteResourceActions } from "utils/store";
import { useToaster } from "utils";
import { ThemeColors } from "consts/theme";
import { BlogCategoriesModal } from "./BlogCategoriesModal";
import { BlogSettingsModal } from "./BlogSettingsModal";
import { AuthorsModal } from "./AuthorsModal";

export const BlogPostsPage = () => {
  return (
    <PageMain>
      <PageRichHeader header="Blog">
        <></>
      </PageRichHeader>
      <PaziaBox>
        <BlogPostsTab />
      </PaziaBox>
    </PageMain>
  );
};

const BlogPostsTab = () => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { searchPhrase, setSearchPhrase } = useFilters();
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange } = useSorting();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isAuthorsModalOpen, setIsAuthorsModalOpen] = useState(false);

  const { isLoading, isFetching, blogPosts, total, invalidate } = useBlogPosts(skip, limit, searchPhrase);

  const onDuplicate = async (row: BlogPostListItemApi) => {
    try {
      await blogApi.duplicateBlogPost(row.id);
      showSuccess("Zduplikowano wpis");
    } catch (error) {
      showError("Nie udało się zduplikować wpisu");
    } finally {
      invalidate();
    }
  };

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase]);

  const columns = React.useMemo<TanTableColumn<BlogPostListItemApi>[]>(() => cols, []);

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <AdminSearchInput onSearch={(searchPhrase: string) => setSearchPhrase(searchPhrase)} className="self-end" />
        <div className="flex flex-wrap items-center gap-2">
          <PaziaButton type="secondary" title="Ustawienia" onClick={() => setIsSettingsModalOpen(true)} />
          <PaziaButton type="secondary" title="Kategorie" onClick={() => setIsCategoriesModalOpen(true)} />
          <PaziaButton type="secondary" title="Autorzy" onClick={() => setIsAuthorsModalOpen(true)} />
          <PaziaButton type="primary" title="Dodaj wpis +" onClick={() => navigate(routes.SHOP_BLOG_ADD)} />
        </div>
      </div>

      <TanTable
        data={blogPosts}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        onDuplicate={onDuplicate}
        pagination={pagination}
        onEdit={(x) => navigate(routes.SHOP_BLOG_EDIT(x.id))}
        sorting={sorting}
        onRowClick={(x) => navigate(routes.SHOP_BLOG_EDIT(x.id))}
        hideBorder
      />
      <AdminResourceDeleteModal
        title="Usuń wpis"
        description="Czy na pewno chcesz usunąć ten wpis? Wszystkie sekcje zostaną również usunięte."
        onSuccessDelete={() => invalidate()}
        deleteAction={blogApi.deleteBlogPost}
      />
      <BlogCategoriesModal isOpen={isCategoriesModalOpen} onClose={() => setIsCategoriesModalOpen(false)} />
      <BlogSettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
      <AuthorsModal isOpen={isAuthorsModalOpen} onClose={() => setIsAuthorsModalOpen(false)} />
    </>
  );
};

const cols: TanTableColumn<BlogPostListItemApi>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 60,
    cell: (info) => info.getValue(),
  },
  {
    id: "title",
    accessorKey: "title",
    header: "Tytuł",
    size: 300,
    enableColumnFilter: false,
  },
  {
    id: "slug",
    accessorKey: "slug",
    header: "Slug",
    size: 200,
    enableColumnFilter: false,
    cell: (info) => (
      <code className="rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">/{info.getValue<string>()}</code>
    ),
  },
  {
    id: "categories",
    accessorKey: "categories",
    header: "Kategorie",
    size: 200,
    enableColumnFilter: false,
    cell: (info) => (
      <div className="flex flex-wrap gap-1">
        {(info.getValue<BlogPostListItemApi["categories"]>() ?? []).map((category) => (
          <PaziaBadge key={category.id} color="indigo">
            {category.name}
          </PaziaBadge>
        ))}
      </div>
    ),
  },
  {
    id: "isPublished",
    accessorKey: "isPublished",
    header: "Status",
    size: 120,
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue<boolean>() ? (
        <PaziaBadge color={"emerald" as ThemeColors}>Opublikowany</PaziaBadge>
      ) : (
        <PaziaBadge color={"slate" as ThemeColors}>Szkic</PaziaBadge>
      ),
  },
  {
    id: "publishedDate",
    accessorKey: "publishedDate",
    header: "Data publikacji",
    cell: (info) => {
      const value = info.getValue<Date | string | undefined>();
      return value ? new Date(value).toLocaleDateString("pl-PL") : "-";
    },
    size: 150,
    enableColumnFilter: false,
  },
];
